import React, { useState, useEffect } from "react";

import { formatDistanceToNow } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";

import {
  Delete,
  Autorenew,
  Storage,
  CheckCircle,
  Error as ErrorIcon,
  AccessTime,
  Code as CodeIcon,
  Apps as AppsIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  CloudOff,
  ContentCopy,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
  Alert,
  AlertTitle,
  IconButton,
} from "@mui/material";

import CostPanel from "../components/costPanel";
import DeleteDialog from "../components/deleteDialog";
import Loader from "../components/loader";
import ServiceStatusList from "../components/serviceStatusList";
import useCluster from "../hooks/cluster";
import { useSnackbar } from "notistack";

export const SingleCluster = () => {
  const navigate = useNavigate();
  const { cluster } = useParams();
  const {
    loading,
    cluster: c,
    deleteCluster,
    refreshCluster,
    apps,
    kubeconfig,
    error,
    useOidc,
    setUseOidc,
  } = useCluster(cluster || "");
  const { enqueueSnackbar } = useSnackbar();
  const [showConf, setShowConf] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (showConf && !kubeconfig) {
      enqueueSnackbar("Unable to retrieve kubeconfig. Please try refreshing the cluster.", {
        variant: "warning"
      });
    }
  }, [showConf, kubeconfig, cluster, enqueueSnackbar]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <CloudOff sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Cluster Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            The cluster "{cluster}" does not exist or you don't have access to it.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              onClick={() => navigate("/clusters")}
              sx={{ borderRadius: 2 }}
            >
              View All Clusters
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/cluster/new")}
              sx={{ borderRadius: 2 }}
            >
              Create New Cluster
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  if (!c || !c.name) {
    return <Loader text="Loading or cluster missing" />;
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Open delete dialog
  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  // Execute cluster deletion
  const confirmDeleteCluster = () => {
    deleteCluster(c.name).then(() => {
      // Navigate back to cluster list on success
      navigate("/clusters");
    });

    // Close dialog
    setDeleteDialogOpen(false);
  };

  // Handle dialog close
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  // Handle kubeconfig visibility toggle
  const toggleKubeconfig = () => {
    if (!showConf && !kubeconfig) {
      enqueueSnackbar("Unable to display kubeconfig. Please try again later.", {
        variant: "error"
      });
    }
    setShowConf(!showConf);
  };

  // Handle copying kubeconfig to clipboard
  const copyKubeconfig = async () => {
    if (!kubeconfig) {
      enqueueSnackbar("No kubeconfig available to copy", {
        variant: "error"
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(kubeconfig);
      enqueueSnackbar("Kubeconfig copied to clipboard", {
        variant: "success"
      });
    } catch (error) {
      enqueueSnackbar("Failed to copy kubeconfig", {
        variant: "error"
      });
    }
  };

  // Format cluster status
  const getStatusChip = () => {
    if (c?.micro?.stage === "done") {
      return (
        <Chip
          icon={<CheckCircle />}
          label="Running"
          color="success"
          sx={{
            fontWeight: "medium",
            maxWidth: "100%",
          }}
        />
      );
    } else if (c?.micro?.failures?.length) {
      return (
        <Chip
          icon={<ErrorIcon />}
          label="Error"
          color="error"
          sx={{
            fontWeight: "medium",
            maxWidth: "100%",
          }}
        />
      );
    } else {
      return (
        <Chip
          icon={<AccessTime />}
          label={`Creating: ${c?.micro?.stage || "initializing"}`}
          color="warning"
          sx={{
            fontWeight: "medium",
            maxWidth: "100%",
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />
      );
    }
  };

  // Render failure messages if any
  const failuresSection = c?.micro?.failures?.length ? (
    <Alert
      severity="error"
      sx={{
        mt: 3,
        borderRadius: 2,
        "& .MuiAlert-icon": {
          alignItems: "flex-start",
          pt: 1.5,
        },
      }}
    >
      <AlertTitle>Cluster Failures</AlertTitle>
      <Stack spacing={1}>
        {c.micro.failures.map((fail, index) => (
          <Typography
            key={index}
            variant="body2"
            component="div"
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {fail}
          </Typography>
        ))}
      </Stack>
    </Alert>
  ) : null;

  // Render services section
  const appsSection = (
    <Box sx={{ mt: 3 }}>
      <ServiceStatusList apps={apps || []} cluster={c} />
    </Box>
  );

  // Render kubeconfig section
  const kubeconfigSection = (
    <Box sx={{ mt: 3 }}>
      <Card sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <CodeIcon color="primary" />
              <Typography variant="h6">Kubernetes Configuration</Typography>
            </Stack>
          }
          action={
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={useOidc}
                    onChange={(e) => setUseOidc(e.target.checked)}
                    color="primary"
                  />
                }
                label="Use OIDC auth"
                sx={{ mr: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showConf}
                    onChange={toggleKubeconfig}
                    color="primary"
                  />
                }
                label="Show kubeconfig"
                sx={{ mr: 1 }}
              />
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          {showConf ? (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {useOidc ? "OIDC-based authentication" : "Certificate-based authentication"}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopy />}
                  onClick={copyKubeconfig}
                  sx={{ borderRadius: 1 }}
                >
                  Copy to Clipboard
                </Button>
              </Stack>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "background.default",
                  border: "1px solid",
                  borderColor: "divider",
                  maxHeight: 400,
                  overflow: "auto",
                  position: "relative",
                }}
              >
                {kubeconfig ? (
                  <Box
                    component="pre"
                    sx={{ m: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}
                  >
                    {kubeconfig}
                  </Box>
                ) : (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    <AlertTitle>Failed to load kubeconfig</AlertTitle>
                    <Typography variant="body2">
                      Unable to retrieve kubeconfig data. Please try refreshing the cluster.
                    </Typography>
                  </Alert>
                )}
              </Paper>
            </Box>
          ) : (
            <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: 2 }}>
              Toggle the switch to view your Kubernetes configuration for
              connecting to this cluster.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  // Main cluster controls
  const clusterControls = (
    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
      <Button
        variant="outlined"
        startIcon={<Autorenew />}
        onClick={() => {
          refreshCluster(c.name).catch((err) => {
            enqueueSnackbar(`Failed to refresh cluster ${c.name}: ${err.message}`, {
              variant: "error"
            });
          });
        }}
        sx={{ borderRadius: 2 }}
      >
        Refresh Cluster
      </Button>

      <Button
        variant="outlined"
        color="error"
        startIcon={<Delete />}
        onClick={openDeleteDialog}
        sx={{
          borderRadius: 2,
          borderColor: "#d32f2f",
          color: "#d32f2f",
          "&:hover": {
            borderColor: "#b71c1c",
            backgroundColor: "rgba(211, 47, 47, 0.04)",
          },
        }}
      >
        Delete Cluster
      </Button>
    </Stack>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Delete dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDeleteCluster}
        title="Delete Cluster"
        itemName={c.name}
      />

      {/* Cluster Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 1.75,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          boxShadow: "0 1px 0 rgba(16, 26, 47, 0.04), 0 1px 2px rgba(16, 26, 47, 0.06)",
          mb: 4,
        }}
      >
        <Stack spacing={3} sx={{ mb: 1 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "start", sm: "center" }}
          >
            <Box>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Storage sx={{ color: "primary.main", fontSize: 28 }} />
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: "'IBM Plex Serif', ui-serif, Georgia, serif",
                      fontWeight: 500,
                      fontSize: { xs: "1.75rem", md: "2.25rem" },
                      letterSpacing: "-0.02em",
                      color: "text.primary",
                    }}
                  >
                    {c.name}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {getStatusChip()}
                  <Typography
                    sx={{
                      fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    created {formatDistanceToNow(new Date(c.created))} ago
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>

          <CostPanel pennies={c.micro.usage} createdAt={c.created} />
        </Stack>

        {/* Show navigation tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="cluster management tabs"
            sx={{
              "& .MuiTab-root": {
                minWidth: "auto",
                px: 3,
                py: 1.5,
                borderRadius: "8px 8px 0 0",
                "&.Mui-selected": {
                  color: "primary.main",
                },
              },
            }}
          >
            <Tab
              icon={<AppsIcon />}
              iconPosition="start"
              label="Applications"
            />
            <Tab
              icon={<CodeIcon />}
              iconPosition="start"
              label="Configuration"
            />
            <Tab
              icon={<SettingsIcon />}
              iconPosition="start"
              label="Settings"
            />
          </Tabs>
        </Box>
      </Paper>

      {/* Failure messages */}
      {failuresSection}

      {/* Tab content */}
      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && appsSection}
        {activeTab === 1 && kubeconfigSection}
        {activeTab === 2 && (
          <Card sx={{ borderRadius: 2 }}>
            <CardHeader
              title={
                <Stack direction="row" spacing={1} alignItems="center">
                  <SettingsIcon color="primary" />
                  <Typography variant="h6">Cluster Settings</Typography>
                </Stack>
              }
            />
            <Divider />
            <CardContent>{clusterControls}</CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default SingleCluster;
